import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { useDreamstate } from '../../data/dashboard';
import type { DreamEntry, DreamNight } from '../../data/dashboard';
import DreamReviewSection, { isDesktopApp } from './DreamReviewSection';
import { useCollapsed, PanelTitle } from './PanelCollapse';

const MONO = REXY_FONT;
const DREAM_OK = '#00FF9C';

// ─────────────────────────────────────────────────────────────────────────────
// DREAMSTATE — HER NIGHTLY TIER-1 SYNTHESIS (Step 3 Piece 3, Part 1 read side).
// Overnight she reasons her day into Tier-1 understanding (SYNTHESIS entries in
// memory/homepage.json, writer REXY_DREAMSTATE). This panel MIRRORS Tier 1 — it
// never synthesizes: nights newest-first, each run's entries VERBATIM; a night
// that ran empty is a fact, not a failure; unattributed Tier-1 entries (no run
// claims them) surface in their own group, never hidden. The gate strip counts
// Tier-1 total + LoRA batch progress toward 2000. Compose lands in Part 2.
// ─────────────────────────────────────────────────────────────────────────────

// DESKTOP ARRANGEMENT ONLY. The wiring moved to data/dashboard/useDreamstate.ts
// under the 2026-07-31 "one core, every window" ruling; the render is unchanged.
// Types re-exported so existing importers of this module keep resolving.
export type {
  DreamEntry, DreamRun, DreamNight, DreamstateGate, DreamstateView, DreamstateResponse,
} from '../../data/dashboard';

const STATUS: Record<DreamNight['status'], { glyph: string; text: string; color: string }> = {
  ran: { glyph: '✓', text: 'ran', color: DREAM_OK },
  ran_empty: { glyph: '○', text: 'ran — honestly empty', color: REXY_CONSOLE.dimText },
  failed: { glyph: '✗', text: 'failed', color: REXY_CONSOLE.critical },
  pending: { glyph: '◔', text: 'pending — tonight covers it', color: REXY_CONSOLE.gold },
};

/** `collapseKey`: the homepage mount's fold control and its stored choice (PanelCollapse). */
export default function DreamstatePanel({ collapseKey }: { collapseKey?: string } = {}) {
  const { view, err, updatedAt, timeLabel, stampLabel } = useDreamstate();
  const [collapsed, toggle] = useCollapsed(collapseKey);

  function renderEntry(entry: DreamEntry) {
    return (
      <div key={entry.id} style={styles.entryRow} title={entry.id}>
        {entry.created_at ? (
          <div style={styles.entryStamp}>{timeLabel(entry.created_at)}</div>
        ) : null}
        {/* Her Tier-1 understanding, VERBATIM — the panel mirrors, never reshapes. */}
        <div style={styles.entryText}>{entry.content}</div>
      </div>
    );
  }

  function renderNight(night: DreamNight) {
    const st = STATUS[night.status];
    const n = night.entries.length;
    return (
      <div key={night.night} style={styles.night} title={night.run?.run_id || night.night}>
        <div style={styles.nightHeader}>
          <span style={styles.nightLabel}>{night.night}</span>
          <span style={{ ...styles.glyph, color: st.color }}>{st.glyph}</span>
          <span style={{ ...styles.status, color: st.color }}>{st.text}</span>
          {n > 0 ? <span style={styles.count}>{n} entr{n === 1 ? 'y' : 'ies'}</span> : null}
        </div>
        {night.run && (night.run.window_start || night.run.window_end) ? (
          <div style={styles.windowRow}>
            <span style={styles.windowSpan}>
              {night.run.window_start ? timeLabel(night.run.window_start) : 'start'}
              {' → '}
              {night.run.window_end ? timeLabel(night.run.window_end) : '?'}
            </span>
          </div>
        ) : null}
        {night.status === 'failed' ? (
          <div style={styles.failure}>{night.run?.error || 'run failed'}</div>
        ) : null}
        {n > 0 ? <div style={styles.entryList}>{night.entries.map(renderEntry)}</div> : null}
      </div>
    );
  }

  const gate = view?.gate;
  return (
    <div style={styles.card}>
      <div style={collapsed ? { ...styles.headerRow, marginBottom: 0 } : styles.headerRow}>
        <PanelTitle title="DREAMSTATE" style={styles.sectionHeader} collapseKey={collapseKey}
                    collapsed={collapsed} onToggle={toggle} />
        <div style={styles.subtitle}>
          nightly {'·'} {view?.timezone || 'world clock'}
          {updatedAt ? <span> {'·'} updated {stampLabel(updatedAt)}</span> : null}
        </div>
      </div>
      {collapsed ? null : (
      <>
      {/* THE OWNER DREAM GATE's review — the desktop app only (owner ruling G12). */}
      {isDesktopApp() ? <DreamReviewSection /> : null}
      {err ? (
        <div style={styles.degraded}>not_available {'—'} {err}</div>
      ) : !view ? (
        <div style={styles.degraded}>loading{'…'}</div>
      ) : view.gate.store_status === 'unreadable' ? (
        <div style={styles.critical}>dreamstate store unreadable {'—'} visible failure</div>
      ) : (
        <>
          {view.nights.length === 0 ? (
            <div style={styles.emptyHistory}>no dreamstate runs yet</div>
          ) : (
            <div style={styles.nights}>{view.nights.map(renderNight)}</div>
          )}
          {/* Tier-1 entries no run claims — surfaced honestly, never hidden. */}
          {view.unattributed.length > 0 ? (
            <div style={styles.unattributed}>
              <div style={styles.unattributedHeader}>
                UNATTRIBUTED {'·'} tier-1 entries no run claims
              </div>
              <div style={styles.entryList}>{view.unattributed.map(renderEntry)}</div>
            </div>
          ) : null}
        </>
      )}
      {gate ? (
        <div style={styles.gateStrip}>
          <span style={styles.gateItem}>TIER 1 {'·'} {gate.tier1_total}</span>
          <span style={styles.gateItem}>
            LORA BATCH {'·'} {gate.lora_progress}/{gate.lora_threshold}
          </span>
          {gate.last_run_completed_at ? (
            <span style={styles.gateDim}>last run {stampLabel(gate.last_run_completed_at)}</span>
          ) : null}
          {gate.tier1_status && gate.tier1_status !== 'ok' ? (
            <span style={styles.gateFail}>tier-1 {gate.tier1_status}</span>
          ) : null}
        </div>
      ) : null}
      </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: { border: `1px solid ${REXY_CONSOLE.cardBorder}`, background: REXY_CONSOLE.cardBg, borderRadius: 6, padding: 14, boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)', minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' },
  // Title sits at the TOP of the panel; the meta strap sits UNDER it and wraps, so it
  // can never displace the title or overflow the card edge (owner ruling 2026-07-24).
  headerRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, marginBottom: 10, minWidth: 0 },
  sectionHeader: { fontWeight: 700, fontFamily: MONO, fontSize: 12, letterSpacing: 2, color: REXY_CONSOLE.primary },
  subtitle: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText, whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: '100%' },
  nights: { display: 'flex', flexDirection: 'column', gap: 10 },
  night: { borderTop: `1px solid ${REXY_CONSOLE.rowDivider}`, padding: '8px 4px 4px 4px' },
  nightHeader: { display: 'flex', alignItems: 'baseline', gap: 8 },
  nightLabel: { fontFamily: MONO, fontSize: 11, color: REXY_CONSOLE.primary, minWidth: 78, whiteSpace: 'nowrap' },
  glyph: { fontFamily: MONO, fontSize: 11 },
  status: { fontFamily: MONO, fontSize: 9, letterSpacing: 1 },
  count: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText, marginLeft: 'auto' },
  windowRow: { display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 3 },
  windowSpan: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.mutedText },
  entryList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 },
  entryRow: { borderLeft: `2px solid ${REXY_CONSOLE.divider}`, paddingLeft: 8, paddingBottom: 2 },
  entryStamp: { fontFamily: MONO, fontSize: 8, color: REXY_CONSOLE.dimText, marginBottom: 2 },
  entryText: { fontFamily: MONO, fontSize: 10, lineHeight: 1.55, color: REXY_CONSOLE.infText, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  unattributed: { borderTop: `1px solid ${REXY_CONSOLE.rowDivider}`, marginTop: 10, padding: '8px 4px 4px 4px' },
  unattributedHeader: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.warning },
  failure: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.critical, marginTop: 4 },
  gateStrip: { display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${REXY_CONSOLE.divider}` },
  gateItem: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.mutedText, whiteSpace: 'nowrap' },
  gateDim: { fontFamily: MONO, fontSize: 8, letterSpacing: 1, color: REXY_CONSOLE.dimText, marginLeft: 'auto', whiteSpace: 'nowrap' },
  gateFail: { fontFamily: MONO, fontSize: 8, letterSpacing: 1, color: REXY_CONSOLE.critical, whiteSpace: 'nowrap' },
  critical: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.critical, padding: '4px 0' },
  degraded: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText, padding: '4px 0' },
  emptyHistory: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText, padding: '4px 0' },
};
